import React, { useCallback, useEffect, useState } from 'react';
import { adminCorporateUserService } from 'api/services/AdminCorporateUserService';
import { cityService, districtService } from 'api';
import type {
  AdminCorporateUserDetailDto,
  AdminCorporateUserListItemDto,
  CorporateApprovalStatus,
} from 'api/types/corporate';
import {
  CORPORATE_APPROVAL_STATUS_LABELS,
  CORPORATE_COMPANY_TYPE_LABELS,
} from 'api/types/corporate';
import type { CityViewModel } from 'api/types/city';
import type { DistrictViewModel } from 'api/types/district';
import Button from 'components/Button';
import { useCorporateUserApproval } from 'hooks/useCorporateUserApproval';

const STATUS_FILTERS: { value: '' | CorporateApprovalStatus; label: string }[] = [
  { value: '', label: 'Tümü' },
  { value: 'Pending', label: 'Onay bekliyor' },
  { value: 'Approved', label: 'Onaylı' },
  { value: 'Rejected', label: 'Reddedildi' },
  { value: 'Suspended', label: 'Askıya alındı' },
];

const CorporateUserManagementPage: React.FC = () => {
  const [items, setItems] = useState<AdminCorporateUserListItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | CorporateApprovalStatus>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminCorporateUserDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [adminFields, setAdminFields] = useState({
    companyAddress: '',
    companyActivityCode: '',
    companyTradeTitle: '',
    naceCode: '',
    kepAddress: '',
  });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rejectionReasonDraft, setRejectionReasonDraft] = useState('');
  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [districts, setDistricts] = useState<DistrictViewModel[]>([]);
  const { approve, reject, suspend, loading: actionLoading, error: actionError } =
    useCorporateUserApproval();

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminCorporateUserService.list({
        approvalStatus: statusFilter || undefined,
        search: search.trim() || undefined,
        pageNumber: page,
        pageSize: 20,
      });
      if (result.isSuccess && result.data) {
        setItems(result.data.items ?? []);
        setTotalPages(result.data.totalPages || 1);
      } else {
        setItems([]);
        setError(result.message ?? 'Liste yüklenemedi.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Liste yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }).then((r) => {
      if (r.isSuccess && r.data) setCities(r.data);
    });
  }, []);

  useEffect(() => {
    if (!detail?.taxOfficeCityId) {
      setDistricts([]);
      return;
    }
    districtService.getAll({ cityId: detail.taxOfficeCityId }).then((r) => {
      if (r.isSuccess && r.data) setDistricts(r.data);
    });
  }, [detail?.taxOfficeCityId]);

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const result = await adminCorporateUserService.getById(id);
      if (result.isSuccess && result.data) {
        setDetail(result.data);
        setAdminFields({
          companyAddress: result.data.companyAddress ?? '',
          companyActivityCode: result.data.companyActivityCode ?? '',
          companyTradeTitle: result.data.companyTradeTitle ?? '',
          naceCode: result.data.naceCode ?? '',
          kepAddress: result.data.kepAddress ?? '',
        });
      } else {
        setError(result.message ?? 'Detay yüklenemedi.');
        setSelectedId(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Detay yüklenemedi.');
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setShowDeleteModal(false);
  };

  const handleDeleteRejected = async () => {
    if (!selectedId || deleteLoading) return;
    setDeleteLoading(true);
    try {
      const result = await adminCorporateUserService.deleteRejected(selectedId);
      if (result.isSuccess) {
        setShowDeleteModal(false);
        closeDetail();
        await loadList();
      } else {
        window.alert(result.message ?? 'Hesap silinemedi.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSaveAdminFields = async () => {
    if (!selectedId) return;
    const result = await adminCorporateUserService.patchAdminFields(selectedId, adminFields);
    if (result.isSuccess) {
      await openDetail(selectedId);
    } else {
      setError(result.message ?? 'Alanlar kaydedilemedi.');
    }
  };

  const runAction = async (action: 'approve' | 'suspend') => {
    if (!selectedId) return;
    const fn = action === 'approve' ? approve : suspend;
    const result = await fn(selectedId);
    if (result.isSuccess) {
      closeDetail();
      void loadList();
    }
  };

  const handleRejectSubmit = async () => {
    if (!selectedId || !rejectionReasonDraft.trim()) return;
    const result = await reject(selectedId, rejectionReasonDraft.trim());
    if (result.isSuccess) {
      setShowRejectModal(false);
      setRejectionReasonDraft('');
      closeDetail();
      void loadList();
    }
  };

  const cityName = cities.find((c) => c.id === detail?.taxOfficeCityId)?.name;
  const districtName = districts.find((d) => d.id === detail?.taxOfficeDistrictId)?.name;

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Kurumsal Kullanıcı Yönetimi</h1>
      </div>

      {(error || actionError) && (
        <div className="alert alert-danger" role="alert">
          {error ?? actionError}
        </div>
      )}

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <select
            className="form-select form-select-sm"
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value as '' | CorporateApprovalStatus);
            }}
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.label} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-5">
          <input
            className="form-control form-control-sm"
            placeholder="E-posta veya marka adı ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                void loadList();
              }
            }}
          />
        </div>
        <div className="col-md-3">
          <Button
            className="btn btn-primary btn-sm w-100"
            type="button"
            onClick={() => {
              setPage(1);
              void loadList();
            }}
          >
            Ara
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-muted py-4 text-center">
          <span className="spinner-border spinner-border-sm me-2" role="status" />
          Yükleniyor…
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-striped align-middle">
            <thead>
              <tr>
                <th>Marka</th>
                <th>E-posta</th>
                <th>Vergi No</th>
                <th>Tür</th>
                <th>Durum</th>
                <th>Kayıt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    Kayıt yok.
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.companyName}</td>
                    <td className="small">{row.email}</td>
                    <td className="small">{row.taxNumber}</td>
                    <td className="small">{CORPORATE_COMPANY_TYPE_LABELS[row.companyType]}</td>
                    <td>
                      <span className="badge bg-secondary">
                        {CORPORATE_APPROVAL_STATUS_LABELS[row.approvalStatus]}
                      </span>
                    </td>
                    <td className="small text-nowrap">
                      {row.createdAtUtc
                        ? new Date(row.createdAtUtc).toLocaleDateString('tr-TR')
                        : '—'}
                    </td>
                    <td>
                      <Button
                        className="btn btn-outline-primary btn-sm"
                        type="button"
                        onClick={() => void openDetail(row.id)}
                      >
                        İncele
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <nav className="mt-2">
          <ul className="pagination pagination-sm mb-0">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                <button type="button" className="page-link" onClick={() => setPage(p)}>
                  {p}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {selectedId && (
        <div
          className="modal show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Kurumsal kullanıcı detayı</h5>
                <button type="button" className="btn-close" onClick={closeDetail} />
              </div>
              <div className="modal-body">
                {detailLoading || !detail ? (
                  <div className="text-center py-4 text-muted">Yükleniyor…</div>
                ) : (
                  <>
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <strong>Marka:</strong> {detail.companyName}
                      </div>
                      <div className="col-md-6">
                        <strong>Durum:</strong>{' '}
                        {CORPORATE_APPROVAL_STATUS_LABELS[detail.approvalStatus]}
                      </div>
                      <div className="col-md-6">
                        <strong>Yetkili:</strong> {detail.firstName} {detail.lastName}
                      </div>
                      <div className="col-md-6">
                        <strong>E-posta:</strong> {detail.email}
                      </div>
                      <div className="col-md-6">
                        <strong>Telefon:</strong> {detail.phoneNumber}
                      </div>
                      <div className="col-md-6">
                        <strong>Vergi no:</strong> {detail.taxNumber}
                      </div>
                      <div className="col-md-6">
                        <strong>Şirket türü:</strong>{' '}
                        {CORPORATE_COMPANY_TYPE_LABELS[detail.companyType]}
                      </div>
                      {detail.nationalNumber && (
                        <div className="col-md-6">
                          <strong>TCKN:</strong> {detail.nationalNumber}
                        </div>
                      )}
                      <div className="col-md-6">
                        <strong>Vergi dairesi:</strong> {cityName ?? detail.taxOfficeCityId} /{' '}
                        {districtName ?? detail.taxOfficeDistrictId}
                      </div>
                      {detail.kepAddress && (
                        <div className="col-md-6">
                          <strong>KEP:</strong> {detail.kepAddress}
                        </div>
                      )}
                    </div>

                    {detail.latestRejectionReason && (
                      <div className="alert alert-warning py-2 small mb-3" role="status">
                        <strong>Son red gerekçesi:</strong> {detail.latestRejectionReason}
                      </div>
                    )}

                    {detail.applicationHistory && detail.applicationHistory.length > 0 && (
                      <div className="mb-3">
                        <h6>Başvuru geçmişi</h6>
                        <div className="table-responsive">
                          <table className="table table-sm table-striped mb-0">
                            <thead>
                              <tr>
                                <th>Tarih</th>
                                <th>Durum</th>
                                <th>Gerekçe</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.applicationHistory.map((item) => (
                                <tr key={item.id}>
                                  <td className="small text-nowrap">
                                    {new Date(item.changedAtUtc).toLocaleString('tr-TR')}
                                  </td>
                                  <td className="small">
                                    {CORPORATE_APPROVAL_STATUS_LABELS[item.status]}
                                  </td>
                                  <td className="small">{item.rejectionReason ?? '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {detail.taxCertificateImageUrl && (
                      <div className="mb-3">
                        <strong>Vergi levhası</strong>
                        <div className="mt-2">
                          <img
                            alt="Vergi levhası"
                            className="img-fluid border rounded"
                            src={detail.taxCertificateImageUrl}
                            style={{ maxHeight: 320 }}
                          />
                        </div>
                      </div>
                    )}

                    <hr />
                    <h6>Yönetici alanları</h6>
                    <div className="row g-2">
                      <div className="col-12">
                        <label className="form-label small">Ticari unvan</label>
                        <input
                          className="form-control form-control-sm"
                          value={adminFields.companyTradeTitle}
                          onChange={(e) =>
                            setAdminFields((f) => ({ ...f, companyTradeTitle: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Adres</label>
                        <input
                          className="form-control form-control-sm"
                          value={adminFields.companyAddress}
                          onChange={(e) =>
                            setAdminFields((f) => ({ ...f, companyAddress: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Faaliyet kodu</label>
                        <input
                          className="form-control form-control-sm"
                          value={adminFields.companyActivityCode}
                          onChange={(e) =>
                            setAdminFields((f) => ({ ...f, companyActivityCode: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">NACE kodu</label>
                        <input
                          className="form-control form-control-sm"
                          value={adminFields.naceCode}
                          onChange={(e) =>
                            setAdminFields((f) => ({ ...f, naceCode: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">KEP adresi</label>
                        <input
                          className="form-control form-control-sm"
                          value={adminFields.kepAddress}
                          onChange={(e) =>
                            setAdminFields((f) => ({ ...f, kepAddress: e.target.value }))
                          }
                        />
                      </div>
                      <div className="col-12">
                        <Button
                          className="btn btn-outline-secondary btn-sm"
                          type="button"
                          onClick={() => void handleSaveAdminFields()}
                        >
                          Yönetici alanlarını kaydet
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <Button className="btn btn-secondary btn-sm" type="button" onClick={closeDetail}>
                  Kapat
                </Button>
                {detail && detail.approvalStatus !== 'Approved' && (
                  <Button
                    className="btn btn-success btn-sm"
                    type="button"
                    disabled={actionLoading}
                    onClick={() => void runAction('approve')}
                  >
                    Onayla
                  </Button>
                )}
                {detail && detail.approvalStatus !== 'Rejected' && (
                  <Button
                    className="btn btn-warning btn-sm"
                    type="button"
                    disabled={actionLoading}
                    onClick={() => {
                      setRejectionReasonDraft('');
                      setShowRejectModal(true);
                    }}
                  >
                    Reddet
                  </Button>
                )}
                {detail && detail.approvalStatus === 'Approved' && (
                  <Button
                    className="btn btn-danger btn-sm"
                    type="button"
                    disabled={actionLoading}
                    onClick={() => void runAction('suspend')}
                  >
                    Askıya al
                  </Button>
                )}
                {detail && detail.approvalStatus === 'Rejected' && (
                  <Button
                    className="btn btn-outline-danger btn-sm"
                    type="button"
                    disabled={deleteLoading}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Hesabı Sil
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && detail && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Kurumsal hesabı sil</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  Bu işlem geri alınamaz. Reddedilmiş kurumsal hesap kalıcı olarak silinecek:
                </p>
                <p className="fw-semibold mb-0">
                  {detail.companyName || detail.email}
                  <span className="text-muted fw-normal"> ({detail.email})</span>
                </p>
                <p className="small text-muted mt-2 mb-0">
                  Aynı e-posta veya vergi numarasıyla yeniden kayıt olunabilir.
                </p>
              </div>
              <div className="modal-footer">
                <Button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                >
                  İptal
                </Button>
                <Button
                  className="btn btn-danger btn-sm"
                  type="button"
                  disabled={deleteLoading}
                  onClick={() => void handleDeleteRejected()}
                >
                  {deleteLoading ? '…' : 'Hesabı Sil'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Başvuruyu reddet</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRejectModal(false)}
                />
              </div>
              <div className="modal-body">
                <label className="form-label" htmlFor="corp-reject-reason">
                  Red gerekçesi
                </label>
                <textarea
                  id="corp-reject-reason"
                  className="form-control"
                  rows={4}
                  value={rejectionReasonDraft}
                  onChange={(e) => setRejectionReasonDraft(e.target.value)}
                  required
                />
              </div>
              <div className="modal-footer">
                <Button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                >
                  İptal
                </Button>
                <Button
                  className="btn btn-warning btn-sm"
                  type="button"
                  disabled={actionLoading || !rejectionReasonDraft.trim()}
                  onClick={() => void handleRejectSubmit()}
                >
                  {actionLoading ? '…' : 'Reddet'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CorporateUserManagementPage;
